import { useState, useEffect } from 'react';
import api from '../../../api/client';

export default function InsightsPage() {
  const [anomalies, setAnomalies] = useState([]);
  const [recs,      setRecs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [running,   setRunning]   = useState(false);
  const [actingId,  setActingId]  = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        api.get('/intelligence/anomalies'),
        api.get('/intelligence/recommendations'),
      ]);
      setAnomalies(aRes.data.data?.items || []);
      setRecs(rRes.data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function runRecommendations() {
    setRunning(true);
    setError(null);
    try {
      await api.post('/intelligence/recommendations/run', { domain: 'hr' });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setRunning(false);
    }
  }

  async function actOnRec(id, action) {
    setActingId(id);
    setError(null);
    try {
      await api.post(`/intelligence/recommendations/${id}/${action}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setActingId(null);
    }
  }

  return (
    <div data-testid="intel-insights-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Insights</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* ── Anomalies ─────────────────────────────────────────────────────── */}
      <section style={{ marginBottom: '2rem' }}>
        <h3>Anomalies</h3>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Domain</th>
                <th style={{ textAlign: 'left' }}>Metric</th>
                <th style={{ textAlign: 'right' }}>Observed</th>
                <th style={{ textAlign: 'right' }}>Expected</th>
                <th style={{ textAlign: 'left' }}>Severity</th>
                <th style={{ textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.map((a) => (
                <tr key={a.id} data-testid="intel-anomaly-row">
                  <td>{a.domain}</td>
                  <td>{a.metric}</td>
                  <td style={{ textAlign: 'right' }}>{a.observed}</td>
                  <td style={{ textAlign: 'right' }}>{a.expected}</td>
                  <td>{a.severity}</td>
                  <td>{a.status}</td>
                </tr>
              ))}
              {!anomalies.length && <tr><td colSpan={6}>No anomalies detected.</td></tr>}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Recommendations ───────────────────────────────────────────────── */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Recommendations</h3>
          <button data-testid="intel-run-recommendations" onClick={runRecommendations} disabled={running}>
            {running ? 'Running...' : 'Run recommendations'}
          </button>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
            {recs.map((r) => (
              <div
                key={r.id}
                data-testid={`intel-recommendation-${r.id}`}
                style={{
                  border: '1px solid #ccc',
                  borderRadius: 8,
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                }}
              >
                <div>
                  <div>{r.message}</div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    score: {typeof r.score === 'number' ? r.score.toFixed(2) : r.score} · status: {r.status}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    data-testid={`intel-recommendation-${r.id}-accept`}
                    onClick={() => actOnRec(r.id, 'accept')}
                    disabled={actingId === r.id || r.status === 'accepted'}
                    style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '0.35rem 0.75rem' }}
                  >
                    Accept
                  </button>
                  <button
                    data-testid={`intel-recommendation-${r.id}-dismiss`}
                    onClick={() => actOnRec(r.id, 'dismiss')}
                    disabled={actingId === r.id || r.status === 'dismissed'}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, padding: '0.35rem 0.75rem' }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
            {!recs.length && <p>No recommendations yet. Run the engine to generate some.</p>}
          </div>
        )}
      </section>
    </div>
  );
}
